// Supabase Connection Status Service
// Monitors connection status and provides status updates

import supabase from './supabaseClient';

class SupabaseConnectionStatus {
  constructor() {
    this.status = 'checking'; // 'connected', 'disconnected', 'checking', 'error'
    this.lastChecked = null;
    this.listeners = [];
    this.checkInterval = null;
    this.isChecking = false;
  }

  // Initialize connection monitoring
  async initialize() {
    await this.checkConnection();
    // Check connection every 30 seconds
    this.checkInterval = setInterval(() => {
      this.checkConnection();
    }, 30000);
  }

  // Check Supabase connection
  async checkConnection() {
    if (this.isChecking) return;
    
    this.isChecking = true;
    this.status = 'checking';
    this.notifyListeners();

    try {
      // Try a simple query to test connection - use chi_primarychinese table
      const { data, error } = await supabase
        .from('chi_primarychinese')
        .select('id')
        .limit(1);

      if (error) {
        throw error;
      }

      this.status = 'connected';
      this.lastChecked = new Date();
      this.notifyListeners();
    } catch (error) {
      console.error('Supabase connection check failed:', error);
      this.status = 'disconnected';
      this.lastChecked = new Date();
      this.notifyListeners();
    } finally {
      this.isChecking = false;
    }
  }

  // Get current status
  getStatus() {
    return {
      status: this.status,
      lastChecked: this.lastChecked,
      isConnected: this.status === 'connected'
    };
  }

  // Subscribe to status changes
  subscribe(callback) {
    this.listeners.push(callback);
    // Immediately call with current status
    callback(this.getStatus());
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners
  notifyListeners() {
    const status = this.getStatus();
    this.listeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('Error in connection status listener:', error);
      }
    });
  }

  // Cleanup
  cleanup() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.listeners = [];
  }
}

// Create singleton instance
const supabaseConnectionStatus = new SupabaseConnectionStatus();

export default supabaseConnectionStatus;

